import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Switch,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Stack,
  useTheme,
  alpha,
  Collapse,
  Fade,
  Slide
} from '@mui/material';
import {
  Close,
  NotificationsActive,
  Email,
  PhoneIphone,
  CheckCircleOutline,
  ErrorOutline,
  AutoAwesome,
  TrendingUp,
  Bolt
} from '@mui/icons-material';
import NotificationService from '../services/NotificationService';
import api from '../services/api';

const NotificationSettingsDialog = ({ open, onClose, onSettingsChange }) => {
  const [savedSettings, setSavedSettings] = useState({
    pushNotifications: false,
    emailNotifications: false
  });
  
  const [localSettings, setLocalSettings] = useState({
    pushNotifications: false,
    emailNotifications: false
  });
  
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pushSupported, setPushSupported] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    const changed = 
      localSettings.pushNotifications !== savedSettings.pushNotifications ||
      localSettings.emailNotifications !== savedSettings.emailNotifications;
    setHasChanges(changed);
  }, [localSettings, savedSettings]);

  const loadCurrentSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const supported = ('Notification' in window) && 
                       ('serviceWorker' in navigator) && 
                       ('PushManager' in window);
      setPushSupported(supported);

      const response = await api.get('/notifications/settings');
      const data = response.data;
      
      const loadedSettings = {
        pushNotifications: Boolean(data.push_notifications) && supported,
        emailNotifications: Boolean(data.email_notifications)
      };
      
      setSavedSettings(loadedSettings);
      setLocalSettings(loadedSettings);
      
    } catch (err) {
      console.error('Failed to load notification settings:', err);
      
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 404) {
        const defaultSettings = { pushNotifications: false, emailNotifications: false };
        setSavedSettings(defaultSettings);
        setLocalSettings(defaultSettings);
      } else {
        setError('Failed to load settings');
        setLocalSettings({ pushNotifications: false, emailNotifications: false });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadCurrentSettings();
    } else {
      setError('');
      setSuccess('');
      setHasChanges(false);
    }
  }, [open, loadCurrentSettings]);

  const handleToggleChange = (type, value) => {
    setLocalSettings(prev => ({ ...prev, [type]: value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      if (localSettings.pushNotifications && pushSupported) {
        const permissionGranted = await NotificationService.requestPermission();
        if (!permissionGranted) {
          setError('Push notification permission was denied');
          setSaveLoading(false);
          setLocalSettings(prev => ({ ...prev, pushNotifications: false }));
          return;
        }
        
        try {
          await NotificationService.subscribeToPush();
        } catch (subscribeError) {
          console.error('Push subscription failed:', subscribeError);
          setError('Failed to set up push notifications');
          setSaveLoading(false);
          setLocalSettings(prev => ({ ...prev, pushNotifications: false }));
          return;
        }
      } else if (!localSettings.pushNotifications && savedSettings.pushNotifications) {
        try {
          await NotificationService.unsubscribeFromPush();
        } catch (unsubError) {
          console.log('Unsubscribe warning:', unsubError);
        }
      }

      const response = await api.post('/notifications/settings', {
        push_notifications: localSettings.pushNotifications && pushSupported,
        email_notifications: localSettings.emailNotifications
      });

      const newSavedSettings = {
        pushNotifications: Boolean(response.data.push_notifications) && pushSupported,
        emailNotifications: Boolean(response.data.email_notifications)
      };
      
      setSavedSettings(newSavedSettings);
      setLocalSettings(newSavedSettings);
      setSuccess('Settings saved successfully!');
      
      if (onSettingsChange) {
        onSettingsChange(newSavedSettings);
      }
      
      setTimeout(() => onClose(), 1500);
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setLocalSettings(savedSettings);
    setError('');
    setSuccess('');
    onClose();
  };

  const hasAnyNotifications = localSettings.pushNotifications || localSettings.emailNotifications;

  // Notification option card component
  const NotificationOption = ({ 
    icon: Icon, 
    title, 
    subtitle, 
    enabled, 
    onChange, 
    disabled, 
    color,
    gradientFrom,
    gradientTo,
    unsupportedText
  }) => (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 3,
        p: 2.5,
        background: enabled 
          ? `linear-gradient(135deg, ${alpha(gradientFrom, 0.15)} 0%, ${alpha(gradientTo, 0.08)} 100%)`
          : isDark ? alpha('#fff', 0.02) : alpha('#000', 0.02),
        border: `1px solid ${enabled ? alpha(color, 0.3) : alpha(isDark ? '#fff' : '#000', 0.08)}`,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        overflow: 'hidden',
        '&:hover': !disabled ? {
          transform: 'translateY(-2px)',
          boxShadow: enabled 
            ? `0 8px 30px ${alpha(color, 0.25)}`
            : `0 8px 30px ${alpha('#000', 0.1)}`,
          border: `1px solid ${enabled ? alpha(color, 0.5) : alpha(color, 0.3)}`,
        } : {},
        '&::before': enabled ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
        } : {}
      }}
      onClick={() => !disabled && onChange(!enabled)}
    >
      {/* Animated glow effect when enabled */}
      {enabled && (
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(color, 0.2)} 0%, transparent 70%)`,
            animation: 'pulse 3s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
              '50%': { transform: 'scale(1.2)', opacity: 0.8 },
            }
          }}
        />
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Icon container with gradient background */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: enabled 
                ? `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`
                : isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08),
              boxShadow: enabled ? `0 4px 15px ${alpha(color, 0.4)}` : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            <Icon sx={{ 
              fontSize: 24, 
              color: enabled ? '#fff' : (isDark ? alpha('#fff', 0.5) : alpha('#000', 0.4)),
              transition: 'all 0.3s ease'
            }} />
          </Box>
          
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 700,
                color: enabled ? color : 'text.primary',
                letterSpacing: '-0.01em',
                transition: 'color 0.3s ease'
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.8rem',
                mt: 0.25
              }}
            >
              {subtitle}
            </Typography>
            {disabled && unsupportedText && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'error.main',
                  fontSize: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5
                }}
              >
                <ErrorOutline sx={{ fontSize: 12 }} />
                {unsupportedText}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Switch
          checked={enabled}
          onChange={(e) => {
            e.stopPropagation();
            onChange(e.target.checked);
          }}
          disabled={disabled || saveLoading}
          sx={{
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                color: '#fff',
                '& + .MuiSwitch-track': {
                  background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
                  opacity: 1,
                },
              },
            },
            '& .MuiSwitch-track': {
              borderRadius: 20,
              backgroundColor: isDark ? alpha('#fff', 0.15) : alpha('#000', 0.15),
            },
            '& .MuiSwitch-thumb': {
              boxShadow: enabled ? `0 2px 8px ${alpha(color, 0.5)}` : '0 2px 4px rgba(0,0,0,0.2)',
            }
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: isDark 
            ? 'linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%)',
          boxShadow: isDark 
            ? '0 25px 80px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1) inset'
            : '0 25px 80px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.05) inset',
          overflow: 'hidden',
          border: isDark ? '1px solid rgba(255,255,255,0.05)' : 'none',
        }
      }}
    >
      {/* Premium Header */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #bf9b30 0%, #f4d03f 50%, #bf9b30 100%)',
          backgroundSize: '200% 200%',
          animation: 'shimmer 3s ease infinite',
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          p: 3,
          pb: 4,
        }}
      >
        {/* Decorative elements */}
        <Box sx={{ position: 'absolute', top: 10, left: 20, opacity: 0.2 }}>
          <AutoAwesome sx={{ fontSize: 40 }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: 15, right: 60, opacity: 0.15 }}>
          <TrendingUp sx={{ fontSize: 60 }} />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              <NotificationsActive sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800, 
                  color: 'white',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  letterSpacing: '-0.02em'
                }}
              >
                Notifications
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500
                }}
              >
                Stay ahead with instant alerts
              </Typography>
            </Box>
          </Box>
          
          <IconButton 
            onClick={handleCancel}
            disabled={saveLoading}
            sx={{ 
              color: 'white',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(255,255,255,0.25)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3, pt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #bf9b30 0%, #f4d03f 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            >
              <CircularProgress size={30} sx={{ color: 'white' }} />
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading your preferences...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Status Messages */}
            <Collapse in={!!error}>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background: alpha('#ef4444', 0.1),
                  border: `1px solid ${alpha('#ef4444', 0.2)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <ErrorOutline sx={{ color: '#ef4444' }} />
                <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 500 }}>
                  {error}
                </Typography>
              </Box>
            </Collapse>

            <Collapse in={!!success}>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.05) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <CheckCircleOutline sx={{ color: '#10b981' }} />
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                  {success}
                </Typography>
              </Box>
            </Collapse>

            {/* Notification Options */}
            <Stack spacing={2}>
              <NotificationOption
                icon={PhoneIphone}
                title="Push Notifications"
                subtitle="Instant alerts directly to your device"
                enabled={localSettings.pushNotifications}
                onChange={(value) => handleToggleChange('pushNotifications', value)}
                disabled={!pushSupported || saveLoading}
                color="#3b82f6"
                gradientFrom="#3b82f6"
                gradientTo="#8b5cf6"
                unsupportedText={!pushSupported ? "Not supported in this browser" : null}
              />
              
              <NotificationOption
                icon={Email}
                title="Email Notifications"
                subtitle="Detailed trade signals in your inbox"
                enabled={localSettings.emailNotifications}
                onChange={(value) => handleToggleChange('emailNotifications', value)}
                disabled={saveLoading}
                color="#8b5cf6"
                gradientFrom="#8b5cf6"
                gradientTo="#ec4899"
              />
            </Stack>

            {/* Status Summary */}
            <Fade in timeout={500}>
              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 3,
                  background: hasAnyNotifications
                    ? 'linear-gradient(135deg, rgba(191, 155, 48, 0.08) 0%, rgba(244, 208, 63, 0.04) 100%)'
                    : isDark ? alpha('#fff', 0.02) : alpha('#000', 0.02),
                  border: `1px solid ${hasAnyNotifications ? alpha('#bf9b30', 0.2) : alpha(isDark ? '#fff' : '#000', 0.05)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: hasAnyNotifications 
                      ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                      : isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08),
                  }}
                >
                  {hasAnyNotifications ? (
                    <Bolt sx={{ color: 'white', fontSize: 24 }} />
                  ) : (
                    <NotificationsActive sx={{ color: isDark ? alpha('#fff', 0.4) : alpha('#000', 0.3), fontSize: 22 }} />
                  )}
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                    {hasAnyNotifications ? "You're all set!" : "Enable notifications"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {hasAnyNotifications 
                      ? `Receiving alerts via ${[
                          localSettings.pushNotifications && 'Push',
                          localSettings.emailNotifications && 'Email'
                        ].filter(Boolean).join(' & ')}`
                      : "Turn on notifications to never miss a trade signal"
                    }
                  </Typography>
                </Box>
                
                {hasAnyNotifications && (
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {localSettings.pushNotifications && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 10,
                          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em'
                        }}
                      >
                        PUSH
                      </Box>
                    )}
                    {localSettings.emailNotifications && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 10,
                          background: 'linear-gradient(90deg, #8b5cf6, #ec4899)',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em'
                        }}
                      >
                        EMAIL
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Fade>
          </>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          p: 3,
          pt: 0,
        }}
      >
        <Button 
          onClick={handleCancel}
          disabled={saveLoading}
          sx={{ 
            px: 3,
            py: 1.25,
            borderRadius: 2,
            color: 'text.secondary',
            fontWeight: 600,
            '&:hover': {
              background: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.05),
            }
          }}
        >
          Cancel
        </Button>
        
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saveLoading || !hasChanges}
          sx={{
            px: 4,
            py: 1.25,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: '0.9rem',
            background: hasChanges 
              ? 'linear-gradient(135deg, #bf9b30 0%, #f4d03f 100%)'
              : isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1),
            color: hasChanges ? 'white' : (isDark ? alpha('#fff', 0.3) : alpha('#000', 0.3)),
            boxShadow: hasChanges ? '0 4px 20px rgba(191, 155, 48, 0.4)' : 'none',
            transition: 'all 0.3s ease',
            '&:hover': hasChanges ? {
              background: 'linear-gradient(135deg, #a68728 0%, #e6c136 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(191, 155, 48, 0.5)',
            } : {},
            '&.Mui-disabled': {
              background: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1),
              color: isDark ? alpha('#fff', 0.3) : alpha('#000', 0.3),
            }
          }}
        >
          {saveLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
              Saving...
            </Box>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default NotificationSettingsDialog;